import React, { useEffect, useState } from 'react'

import { SET_TIP_DATA, TIP_SUBMIT, TIP_SUBMIT_ATTEMPT } from '@actions/types'
import { useSelector, useDispatch } from 'react-redux'
import { selectAccountConfig, selectAnalytics, selectTip, makeSelectIsLoadingType } from '@selectors'

import classNames from 'classnames'

import {
  IconInline,
  Label,
  ResponsiveContainer,
  Paragraph,
  Select,
  Switch,
  Title
} from '@omnilert/components'

import FileDisplay from '../file-display/file-display.component'
import TipInputContainer from '../tip-input-container/tip-input-container.component'

import {
  LABEL_TIP_CATEGORY,
  LABEL_TIP_LOCATION,
  LABEL_TIP_PRIVATE,
  TIP_CATEGORY_DEFAULT
} from '../../assets/i18n/strings'

import i18n from 'i18n-js'

import { ERROR_MODAL_OPEN } from '@redux/actions/types'

import useStyles from './tips.style'

const selectIsSubmittingTip = makeSelectIsLoadingType(TIP_SUBMIT)

const Tips = (props) => {
  const dispatch = useDispatch()

  const Analytics = useSelector(selectAnalytics)

  const tipData = useSelector(selectTip)

  const accountConfig = useSelector(selectAccountConfig)
  
  const tipCategories = accountConfig?.tipping?.tips || []
   
  const hasOneCategory = tipCategories.length === 1

  const { category, anonymous, sendLocation } = tipData

  const [showAnonymous, setShowAnonymous] = useState(false)

  const [helpText, setHelpText] = useState(null)

  const classes = useStyles({ hasOneCategory, ...props })

  const isSubmittingTip = useSelector(selectIsSubmittingTip)

  useEffect(() => {
    if (category || hasOneCategory) {
      const categoryAPI = hasOneCategory ? tipCategories[0] : tipCategories.find(({ name: cn }) => cn === category)
      const { allow_anonymous: allowAnonymous, confirmation, config_hash: configHash, help_text: helpText, scenario_id: scenarioId } = categoryAPI
      
      if (allowAnonymous) setShowAnonymous(true)

      helpText ? setHelpText(helpText) : setHelpText(null)

      dispatch({
        type: SET_TIP_DATA,
        payload: {
          category: categoryAPI.name,
          configHash: configHash,
          scenarioId,
          tipConfirmationText: confirmation || ''
        }
      })
    } else {
      setHelpText(null)
    }
  }, [category, dispatch, hasOneCategory, tipCategories])


  const handleOnChangeTipCategory = e => {
    const { value } = e.target

    dispatch({
      type: SET_TIP_DATA,
      payload: {
        category: value
      }
    })

    Analytics.logEvent('TipCategorySelected')
  }


  const handleInputChange = e => {
    const { target } = e

    const { name, value } = target

    switch (name) {
    case 'sendLocation':
      dispatch({
        type: SET_TIP_DATA,
        payload: {
          sendLocation: !sendLocation
        }
      })
      break
    case 'anonymous':
      dispatch({
        type: SET_TIP_DATA,
        payload: {
          anonymous: !anonymous
        }
      })
      break
    default:
      dispatch({
        type: SET_TIP_DATA,
        payload: {
          [name]: value
        }
      })
    }
  }

  const onFocusTipSelect = () => {
    Analytics.logEvent('TipCategoryAlert')
  }

  const onSubmitTip = e => {
    e.preventDefault()

    if (!category) {
      dispatch({ type: ERROR_MODAL_OPEN, payload: { message: 'Please select a category.' } })
    } else {
      dispatch({ type: TIP_SUBMIT_ATTEMPT })
    }
  }

  return (
    <div className={classes.tipsContainer}>
      <ResponsiveContainer className={classes.responsive}>
        <form
          className={classes.tipForm}
          onSubmit={onSubmitTip}
        >
          <div className={classes.formFieldsContainer}>
            <div className={classNames([classes.tipCategoriesField, classes.field])}>
              <Label>{i18n.t(LABEL_TIP_CATEGORY)}</Label>
              <Select
                className={classes.tipSelect}
                defaultOption={i18n.t(TIP_CATEGORY_DEFAULT)}
                disabled={tipCategories.length === 1}
                name='category'
                onChange={handleOnChangeTipCategory}
                onFocus={onFocusTipSelect}
                optionKey='category'
                options={tipCategories.map(tip => ({
                  label: tip.name,
                  value: tip.name
                }))}
                value={category}
              />
            </div>

            {showAnonymous &&
            <div className={classes.field}>
              <div className={classes.iconTextContainer}>
                <IconInline
                  className={classes.maskIcon}
                  name='mask'
                  size={34}
                />
                <Title
                  className={classes.label}
                  color='var(--tips__text)'
                  size='sm'
                >{i18n.t(LABEL_TIP_PRIVATE)}
                </Title>
              </div>
              <Switch
                checked={!!anonymous}
                id='anonymous'
                name='anonymous'
                onChange={handleInputChange}
                value={anonymous}
              />
            </div>}

            <div className={classes.field}>
              <div className={classes.iconTextContainer}>
                <IconInline
                  name='marker'
                  size={30}
                />
                <Title
                  className={classes.label}
                  color='var(--tips__text)'
                  size='sm'
                >{i18n.t(LABEL_TIP_LOCATION)}
                </Title>
              </div>
              <Switch
                checked={sendLocation}
                id='sendLocation'
                name='sendLocation'
                onChange={handleInputChange}
                value={sendLocation}
              />
            </div>
          </div>

          {helpText &&
          <Paragraph
            className={classes.helpText}
            color='var(--tips__text)'
            size='sm'
          >{helpText}
          </Paragraph>}

          <TipInputContainer
            className={classes.tipsInput}
            handleInputChange={handleInputChange}
            isSubmittingTip={isSubmittingTip}
          />

          <FileDisplay />

        </form>
      </ResponsiveContainer>
    </div>
  )
}

export default Tips
